/**
 * Iter 7 (Enterprise Projects Phase 1) — shared UI primitives.
 *
 * A small palette of consistent, reusable primitives so every Enterprise
 * Projects surface (Projects List, Project Detail, Work Item Detail,
 * Workspaces Panel, Accountability Chain, My Work) renders dates,
 * states, priorities, users, denials, loading and error states the
 * same way. No new business scope, no backend contract changes — this
 * file is 100 % presentational.
 *
 * Pure formatters live in ``formatters.js`` so they can be unit-tested
 * without a JSX transform. This file re-exports them so consumers
 * import from a single place.
 */
import { Link } from "react-router-dom";
import { Loader2, ShieldAlert, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDate, formatDateTime, formatUserName } from "./formatters";


export { formatDate, formatDateTime, formatUserName };


// ─────────────────────────────────────────────────────────────────────
// Formatters — canonical presentation for cross-surface consistency.
// (Implementations live in ``./formatters.js`` and are re-exported above.)
// ─────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────
// Badges — colour maps kept centralised.
// ─────────────────────────────────────────────────────────────────────

const STATE_STYLES = {
    created:         "bg-slate-100 text-slate-700 ring-slate-300",
    in_progress:     "bg-blue-50 text-blue-700 ring-blue-200",
    awaiting_review: "bg-amber-50 text-amber-700 ring-amber-200",
    blocked:         "bg-rose-50 text-rose-700 ring-rose-200",
    completed:       "bg-emerald-50 text-emerald-700 ring-emerald-200",
    cancelled:       "bg-slate-100 text-slate-500 ring-slate-300",
    active:          "bg-emerald-50 text-emerald-700 ring-emerald-200",
    closed:          "bg-slate-100 text-slate-500 ring-slate-300",
};

const STATE_LABELS = {
    created:         "Created",
    in_progress:     "In Progress",
    awaiting_review: "Awaiting Review",
    blocked:         "Blocked",
    completed:       "Completed",
    cancelled:       "Cancelled",
    active:          "Active",
    closed:          "Closed",
};

/** Coloured state pill. Renders "—" for null/undefined states. */
export function StateBadge({ state, testId }) {
    if (!state) return <span className="text-slate-400">—</span>;
    const tone = STATE_STYLES[state] || "bg-slate-100 text-slate-700 ring-slate-300";
    const label = STATE_LABELS[state] || state.replace(/_/g, " ");
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium uppercase tracking-wide ring-1 ${tone}`}
            data-testid={testId}
        >
            {label}
        </span>
    );
}


const PRIORITY_STYLES = {
    urgent: "bg-rose-100 text-rose-700 ring-rose-300",
    high:   "bg-orange-50 text-orange-700 ring-orange-200",
    normal: "bg-slate-100 text-slate-600 ring-slate-300",
    low:    "bg-slate-50 text-slate-500 ring-slate-200",
};

/** Coloured priority pill. Falls back to a neutral tone. */
export function PriorityBadge({ priority, testId }) {
    if (!priority) return <span className="text-slate-400">—</span>;
    const tone = PRIORITY_STYLES[priority] || PRIORITY_STYLES.normal;
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium capitalize ring-1 ${tone}`}
            data-testid={testId}
        >
            {priority}
        </span>
    );
}


// ─────────────────────────────────────────────────────────────────────
// Loading / Error primitives.
// ─────────────────────────────────────────────────────────────────────

/** Centred loading indicator for full-page loading states. */
export function PageLoading({ label = "Loading…", testId = "page-loading" }) {
    return (
        <div
            className="flex items-center gap-2 text-sm text-slate-500 p-6"
            data-testid={testId}
        >
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{label}</span>
        </div>
    );
}

/** Centred error card with an optional back-navigation button. */
export function PageError({ title = "Something went wrong", message, backTo, backLabel = "Go back", testId = "page-error" }) {
    return (
        <div
            className="max-w-lg mx-auto mt-10 p-6 border border-rose-200 bg-rose-50 rounded-md space-y-3"
            data-testid={testId}
        >
            <div className="flex items-center gap-2 text-rose-700 font-semibold">
                <ShieldAlert className="w-4 h-4" />
                <span>{title}</span>
            </div>
            {message && <div className="text-sm text-rose-700">{message}</div>}
            {backTo && (
                <Button asChild variant="outline" size="sm">
                    <Link to={backTo}><ArrowLeft className="w-4 h-4 mr-1" />{backLabel}</Link>
                </Button>
            )}
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────
// Structured authorization denial renderer.
//
// The Iter-4 hardening contract emits (in production mode):
//   { layer, reason_code, message, action }
//
// This component renders those in a friendly, self-explanatory panel
// so users understand WHY they were denied and what to do next,
// without exposing the layer-by-layer trace.
// ─────────────────────────────────────────────────────────────────────

const LAYER_HINTS = {
    capability:              "Your role does not include the permission required for this action.",
    resource:                "The item you tried to reach no longer exists or has been removed.",
    scope:                   "This work is outside the projects you participate in.",
    workspace_membership:    "You are not a member of this workspace.",
    chain_of_accountability: "Only the current owner, contributors, or delegates can perform this action.",
};

export function FriendlyDeniedError({ detail, className = "" }) {
    if (!detail || typeof detail !== "object") return null;
    const { layer, reason_code, message, action } = detail;
    const hint = LAYER_HINTS[layer];
    return (
        <div
            className={`p-3 border border-amber-200 bg-amber-50 rounded-md space-y-1 ${className}`}
            data-testid="friendly-denied-error"
        >
            <div className="flex items-center gap-2 text-amber-800 font-medium text-sm">
                <ShieldAlert className="w-4 h-4" />
                <span>Access not available</span>
            </div>
            {message && (
                <p className="text-sm text-amber-900" data-testid="friendly-denied-message">
                    {message}
                </p>
            )}
            {hint && hint !== message && (
                <p className="text-xs text-amber-700 italic">{hint}</p>
            )}
            {(reason_code || action) && (
                <div className="text-[10px] text-amber-600 font-mono pt-1" data-testid="friendly-denied-tech">
                    {action && <span>{action}</span>}
                    {action && reason_code && <span> · </span>}
                    {reason_code && <span>{reason_code}</span>}
                </div>
            )}
        </div>
    );
}
